import React, { useState, useEffect } from 'react';
import { Heart, Users, HandHeart, Gift, Calendar, MapPin, Mail, CheckCircle, ArrowRight, Sparkles, Shield } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const HR_EMAIL = 'hr@chinesecommunity.ai';

const ServiceCard = ({ icon: Icon, title, description, location, time, category, color, freeLabel }) => {
  const colorMap = {
    blue: 'from-blue-500/20 to-cyan-500/20 text-blue-400',
    red: 'from-rose-500/20 to-pink-500/20 text-rose-400',
    yellow: 'from-amber-500/20 to-orange-500/20 text-amber-400',
    green: 'from-emerald-500/20 to-teal-500/20 text-emerald-400',
  };
  const gradient = colorMap[color] || colorMap.blue;
  return (
    <div className="glow-card bg-dark-card rounded-2xl border border-dark-border p-6 hover:border-dark-border-light transition-all duration-400 group">
      <div className="flex items-start gap-4 mb-4">
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${gradient.split(' ').slice(0, 2).join(' ')} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
          <Icon className={`h-5 w-5 ${gradient.split(' ').pop()}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-semibold text-white">{title}</h3>
            <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-[10px] font-bold rounded-full border border-emerald-500/20">{freeLabel}</span>
          </div>
          {category && <span className="inline-block text-xs text-gray-500 mb-2">{category}</span>}
          <p className="text-sm text-gray-400 leading-relaxed mb-3">{description}</p>
          {location && <div className="flex items-center text-xs text-gray-500"><MapPin className="h-3 w-3 mr-1" />{location}</div>}
          {time && <div className="flex items-center text-xs text-gray-500 mt-1"><Calendar className="h-3 w-3 mr-1" />{time}</div>}
        </div>
      </div>
    </div>
  );
};

const EventCard = ({ title, date, location, description, type, learnMoreLabel }) => (
  <div className="glow-card bg-dark-card rounded-2xl border border-dark-border p-6 hover:border-dark-border-light transition-all duration-400">
    <div className="flex items-center gap-2 mb-3">
      <span className="text-[11px] font-semibold text-accent bg-accent/10 px-2.5 py-1 rounded-full border border-accent/20">{type}</span>
      <span className="text-xs text-gray-500">{date}</span>
    </div>
    <h3 className="text-base font-semibold text-white mb-2">{title}</h3>
    <p className="text-sm text-gray-400 mb-3 leading-relaxed">{description}</p>
    <div className="flex items-center text-xs text-gray-500 mb-4"><MapPin className="h-3 w-3 mr-1" />{location}</div>
    <button className="w-full py-2 bg-dark-surface text-gray-300 rounded-lg font-medium text-sm hover:bg-dark-elevated hover:text-white border border-dark-border transition-all duration-300">{learnMoreLabel}</button>
  </div>
);

const serviceIcons = [Heart, Heart, Heart, Gift, Heart, Users, Gift, Heart, Heart, Heart, Gift, Heart];
const serviceColors = ['red', 'blue', 'blue', 'yellow', 'red', 'yellow', 'green', 'green', 'blue', 'blue', 'yellow', 'red'];

const Community = () => {
  const { t } = useLanguage();
  usePageTitle('Aid Partnership');
  const categories = t('community.categories') || [];
  const servicesList = t('community.services') || [];
  const eventsList = t('community.events') || [];
  const [selectedCategory, setSelectedCategory] = useState(categories[0] || 'All');
  const services = servicesList.map((s, i) => ({ ...s, icon: serviceIcons[i] || Heart, color: serviceColors[i] || 'blue' }));
  const activeCategory = categories.includes(selectedCategory) ? selectedCategory : (categories[0] || 'All');
  const filteredServices = !activeCategory || activeCategory === categories[0] ? services : services.filter(s => s.category === activeCategory);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="-mt-16">
      {/* Hero */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-rose-600/12 via-pink-600/8 to-transparent rounded-full blur-[100px]" />
          <div className="absolute -bottom-20 -left-20 w-[500px] h-[500px] bg-gradient-to-tr from-blue-600/8 to-transparent rounded-full blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center px-3.5 py-1.5 text-[11px] font-semibold tracking-wide uppercase text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-full mb-6">{t('community.inDevelopment')}</span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-5">{t('community.title')}</h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto mb-2">{t('community.subtitle')}</p>
          <p className="text-sm text-gray-500 max-w-2xl mx-auto">{t('community.description')}</p>
          <p className="text-xs text-gray-500 max-w-2xl mx-auto mt-3 italic">{t('community.pageSummary')}</p>
        </div>
      </section>

      {/* Mission Stats */}
      <section className="relative z-20 pb-8 -mt-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border border-dark-border bg-dark-card rounded-2xl shadow-dark-glow px-6 sm:px-10 py-8">
            <div className="text-center mb-6">
              <h2 className="text-lg font-bold text-white tracking-tight">{t('community.missionTitle')}</h2>
              <p className="text-sm text-gray-400 mt-1">{t('community.missionDesc')}</p>
            </div>
            <div className="grid grid-cols-3 divide-x divide-dark-border text-center">
              <div className="px-4"><Heart className="h-6 w-6 mx-auto mb-2 text-accent" /><div className="text-xl font-bold text-white">100%</div><div className="text-xs text-gray-500 mt-1">{t('community.freeService')}</div></div>
              <div className="px-4"><Users className="h-6 w-6 mx-auto mb-2 text-accent" /><div className="text-xl font-bold text-white">{t('community.communityPartners')}</div><div className="text-xs text-gray-500 mt-1">{t('community.localPartners')}</div></div>
              <div className="px-4"><Sparkles className="h-6 w-6 mx-auto mb-2 text-accent" /><div className="text-xl font-bold text-white">{t('community.missionDriven')}</div><div className="text-xs text-gray-500 mt-1">{t('community.serveCommunity')}</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-dot-pattern bg-dot opacity-15" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-2 tracking-tight">{t('community.freeServicesTitle')}</h2>
          <p className="text-sm text-gray-500 mb-6">{t('community.examplesNote')}</p>
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <button key={cat} onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-[13px] font-medium transition-all duration-300 ${activeCategory === cat ? 'bg-accent/15 text-accent border border-accent/25' : 'bg-dark-surface text-gray-400 border border-dark-border hover:text-white hover:border-dark-border-light'}`}>
                {cat}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">{t('community.servicesSection')}</h3>
            <p className="text-sm text-gray-400">{t('community.foundServices', { count: filteredServices.length })}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredServices.map((s, i) => <ServiceCard key={i} {...s} freeLabel={t('community.free')} />)}
          </div>
        </div>
      </section>

      {/* Events */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-white tracking-tight">{t('community.upcomingEvents')}</h2>
            <button className="text-accent font-medium text-sm hover:text-accent-hover flex items-center transition-colors">{t('community.viewAll')} <ArrowRight className="ml-1 h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {eventsList.map((e, i) => <EventCard key={i} {...e} learnMoreLabel={t('community.learnMore')} />)}
          </div>
        </div>
      </section>

      {/* How to Help */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-gradient-to-br from-rose-600/8 to-transparent rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-gradient-to-tl from-emerald-600/8 to-transparent rounded-full blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-14 tracking-tight">{t('community.howToParticipate')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: HandHeart, title: t('community.becomeVolunteer'), desc: t('community.volunteerDesc'), cta: t('community.volunteerCta') },
              { icon: Gift, title: t('community.donate'), desc: t('community.donateDesc'), cta: t('community.donateCta') },
              { icon: Users, title: t('community.seekHelp'), desc: t('community.seekHelpDesc'), cta: t('community.seekHelpCta') },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-dark-surface border border-dark-border rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-7 w-7 text-accent" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400 text-sm mb-6 leading-relaxed">{item.desc}</p>
                <button className="px-6 py-2.5 bg-white text-dark rounded-full font-semibold text-sm hover:shadow-glow-white transition-all duration-400">{item.cta}</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact + Values */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-14 tracking-tight">{t('community.contactUs')}</h2>
          <div className="flex justify-center mb-20">
            <div className="text-center p-6 bg-dark-card rounded-2xl border border-dark-border w-full max-w-sm">
              <Mail className="h-8 w-8 text-accent mx-auto mb-3" />
              <h3 className="font-semibold text-white mb-2">{t('community.emailContact')}</h3>
              <a href={`mailto:${HR_EMAIL}`} className="text-sm text-gray-400 hover:text-accent transition-colors">{HR_EMAIL}</a>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white text-center mb-10 tracking-tight">{t('community.valuesTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: CheckCircle, title: t('community.unity'), desc: t('community.unityDesc') },
              { icon: Heart, title: t('community.mutualAid'), desc: t('community.mutualAidDesc') },
              { icon: Shield, title: t('community.trust'), desc: t('community.trustDesc') },
              { icon: Sparkles, title: t('community.growth'), desc: t('community.growthDesc') },
            ].map((v, i) => (
              <div key={i} className="text-center p-6 bg-dark-card rounded-2xl border border-dark-border glow-card hover:border-dark-border-light transition-all duration-400">
                <v.icon className="h-8 w-8 text-accent mx-auto mb-3" />
                <h3 className="font-semibold text-white mb-2">{v.title}</h3>
                <p className="text-sm text-gray-400">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Community;
